import axios from 'axios';

import React from 'react';

import { Badge, Thumbnail, Icon, Tooltip, Tag, Stack } from '@shopify/polaris';
import {
  EditMinor,
  ChevronRightMinor,
  ChevronDownMinor,
} from '@shopify/polaris-icons';

import { QuantityProgress } from '@presentational';
import StatusLabel from '@presentational/StatusLabel';

import { ta } from './index';
import reject from 'lodash/reject'
import join from 'lodash/join';
import round from 'lodash/round';

const fulfillmentOptions = [
  { label: "-", value: "" },
  { label: "Fulfilled", value: "fulfilled", },
  { label: "OPEN", value: "open", },
  { label: "PARTIALLY_FULFILLED", value: "partially_fulfilled", },
  { label: "RESTOCKED", value: "restocked", },
  { label: "UNFULFILLED", value: "unfulfilled", },
]

const fincancialOptions = [
  { label: "-", value: "" },
  { label: "AUTHORIZED", value: "authorized", },
  { label: "PAID", value: "paid", },
  { label: "PARTIALLY_PAID", value: "partially_paid", },
  { label: "PENDING", value: "pending", },
  { label: "REFUNDED", value: "refunded", },
  { label: "VOIDED", value: "voided", },
]

const orderOptions = [
  { label: "-", value: "" },
  { label: "OPEN", value: "open", },
  { label: "CLOSED", value: "closed", },
  { label: "CANCELLED", value: "cancelled", },
  { label: "ANY", value: "any", },
]

export {
  fulfillmentOptions,
  fincancialOptions,
  orderOptions,
}

export const defaultWarranty = {
  name: "",
  tag: "",
}

export const defaultProduct = {
  title: "",
  description_html: "",
  product_type: "",
  inventory_quantity: "",
  sku: "",
  barcode: "",
  barulu_cost: 0,
  publish_price: 0,
  // taxable: true,
  warranty_id: "",
  images: [],
  local_images: [],
  variants: [],
  // shopify_id: "",
  // status:
  // user_id:
}

export const defaultVariant = {
  title: "",
  sku: "",
  barcode: "",
  weight: 0.0,
  weight_unit: "POUNDS",
  taxable: true,
  price: 0.0,
  inventory_quantity: 0,
  unit_cost: 0,
  // product_id
  // selected_options
  // shopify_id
}

export const defaultProductOption = {
  // name: "color",
  name: "Color",
  values: []
}

export const productTableColumns = [
  "title",
  "inventory_quantity",
  "status",
  "apb",
]

export const defaultUser = {
  email: "",
  first_name: "",
  last_name: "",
  provider: ""
}

export const defaultSeller = {
    address: "",
    bank_account_number: "",
    // default_location_id: "",
    legal_id: "",
    phone_number: "",
    user_id: 1,
    vendor: ""
}

export const defaultTransfer = {
  name: "",
  // products_transfers: [],
  // products_transfers_params: [],
  products_transfers_attributes: [],
}

export const defaultUserSeller = {
  email: "",
  first_name: "",
  last_name: "",
  provider: "",
  address: "",
  bank_account_number: "",
  default_location_id: "",
  legal_id: "",
  phone_number: "",
  vendor: ""
}

export const loadRemoteFiles = async (remoteFiles) => {
  let files = [];

  if (remoteFiles && remoteFiles.length > 0) {
    for (let file of remoteFiles) {
      let response = await axios.get(file.url, {
        headers: {
          "Accept": "application/json, text/plain, */*",
          'Cache-Control': 'no-cache' // Cache is making it fails!
        },
        // crossDomain: true,
        // withCredentials: false,
        responseType: 'blob'
      });
      let { data, status } = response; // data is the file.
      data.name = file.name;
      data.url = file.url;
      files = [
        ...files,
        data
      ]
      // let response = await fetch(file.url,);
      // console.log("Fetch",response);
    }
    console.log("FILES", files);
  }

  return files;
}

// ===================================================================================
// ===================================================================================
// ===================================================================================
//  Tables data
// Size is based on a 12 columns grid

// availables: attention, info, new, success, warning
export const statusSlugs = {
  // Transfer.status (shipment status basically)
  pending: 'attention',
  partial: 'info',
  completed: "success",
  cancelled: 'warning',
  archived: "default",
  
  // Transfer.sale_status
  sale_pending: 'attention',
  sale_partial: 'info',
  billed: 'success',
  sale_completed: 'success',
  sale_cancelled: 'warning',

  // SellerApplicationForm.status
  pre_approved: 'info',
  approved: 'success',
  linked: 'success',
  rejected: 'critical',

  // Brightpearl Order Payment Status
  PAID: 'success',
  PARTIALLY_PAID: "info",
  UNPAID: "attention",
  AUTHORIZED: "info",
  NOT_APPLICABLE: "warning",

  // Product.status
  // pending: 'attention', // same as transfer
  rejected: 'critical',
  published: 'success',
  unpublished: 'attention',
  synced: 'success',
};

/**
 * Receive a columns array and return a new array filtering null values from it and resizing the columns if needed.
 * @param {*} columns 
 * @param {*} resize 
 * @param {string} resizeAttribute
 */
const resizeAndFilterColumns = ({ columns, resize, resizeAttribute }) => {
  if(resize){
    return reject(columns, (item) => item == null).map( (item) => ({ ...item, size: item[resizeAttribute] || item.size }) ); 
  }
  else{
    return columns
  }
}

export const transfersColumns = (isAdmin) => {
  const columns = [
    { size: 1, header: "Entrega", accessor: "name" },

    isAdmin ? { size: 2, header: ta("transfer.vendor"), accessor: "vendor" } : null, // -2 for seller

    { size: 2, header: ta("general.created_at"), accessor: (transfer) => I18n.l("date.formats.unimart", transfer.created_at) },
    { size: 2, header: ta("transfer.invoice_date"), accessor: (transfer) => transfer.invoice_date && I18n.l("date.formats.unimart", transfer.invoice_date) },
    { size: 3, header: ta("transfer.invoice_number"), accessor: "invoice_number" },
    { size: 1, sellerSize: 2, header: ta("transfer.invoice_amount"), accessor: "invoice_amount_money" }, // +1 for seller
    { size: 1, sellerSize: 2, header: ta("transfer.status"), accessor: ({ status }) => ( // +1 for seller
      <Badge status={ statusSlugs[status] }>{ I18n.t(`enum.status.transfer.${status}`) }</Badge>
    )}
  ]
  return resizeAndFilterColumns({ columns, resize: !isAdmin, resizeAttribute: "sellerSize" })
}

// same as productsTransferColumns (used on #show)
// Used on form as well except by columns that got a formSize of 0. Form currencty got a plus size of 3 to allocate
export const transferItemColumns = [
  { size: 1,  header:  ta("products_transfer.shipped"), accessor: ({ quantity, shipped }) => `${shipped}/${quantity}`, formSize: 0 },
  { size: 1,  header:  ta("variant.image"), accessor: ({ variant }) => (
    <Thumbnail alt="Item image" source={ variant.image_url } />
  )},
  { size: 2,  header:  ta("variant.sku"), accessor: ({ variant }) => variant.sku, formSize: 2 }, // form extra 1
  { size: 2,  header:  ta("variant.title"), accessor: ({ variant }) => variant.display_name || variant.title },
  { size: 1,  header:  ta("products_transfer.quantity"), accessor: "quantity" },
  { size: 1,  header:  ta("products_transfer.unit_cost"), accessor: "unit_cost_money", formSize: 2 }, // form extra 1
  { size: 1,  header:  ta("products_transfer.tax_rate"), accessor: ({ tax_rate }) => `${round(tax_rate)}%` },
  { size: 1,  header:  ta("products_transfer.total_amount_with_tax"), accessor: "total_amount_with_tax_money", formSize: 3 }, // form extra 1
  { size: 2,  header:  ta("variant.barcode"), accessor: ({ variant }) => variant.barcode, removeOnForm: true, formSize: 0 },
];

export const transferPaymentColumns = (isAdmin) => {
  const columns = [
    { size: 1, header: "Entrega", accessor: "name" },

    isAdmin ? { size: 1, header: ta("transfer.vendor"), accessor: "vendor" } : null,

    { size: 2, header: ta("transfer.invoice_date"), accessor: (transfer) => transfer.invoice_date && I18n.l("date.formats.unimart", transfer.invoice_date) },
    { size: 2, header: ta("transfer.invoice_number"), accessor: "invoice_number" },
    { size: 2, header: ta("transfer.invoice_amount"), accessor: "brightpearl_payment_total_money" }, // Should match local transfer.invoice_amount
    { size: 2, header: ta("transfer.brightpearl_payment_total_paid"), accessor: "brightpearl_payment_total_paid_money" },
    { size: 1, header: ta("transfer.brightpearl_payment_status"), accessor: ({ brightpearl_payment_status }) => {
      if(brightpearl_payment_status == null){
        return null
      }
      else{
        return <Badge status={ statusSlugs[brightpearl_payment_status] }>{ I18n.t(`enum.brightpearl.order_payment_status.${brightpearl_payment_status}`) }</Badge>
      }
    }},
    { size: 1, header: "", accessor: (_item, utils) => <Icon source={ utils.expandedContent ?  ChevronDownMinor : ChevronRightMinor  } /> } // only for display reasons
  ]

  return reject(columns, (item) => item == null); // filter out vendor if required
}

// Displayed nested on transfer items!
export const transferPaymentJournalColumns = [
  { size: 1, header: "Asiento", accessor: "journal_id" },
  { size: 2, header: ta("general.date"), accessor: "tax_date_human" },
  { size: 4, header: ta("general.description"), accessor: "description" },
  { size: 2, header: ta("general.currency"), accessor: "currency_code" },
  { size: 3, header: "Pagado", accessor: "transaction_debit_money" },
]

export const productsColumns = (isAdmin) => {
  const columns = [
    { size: 1, header: "", accessor: (record) =>
      <Thumbnail
        source={ record.featured_image }
        alt="Featured Image"
      />
    },
    
    {
      size: 4, sellerSize: 6, header: ta("product.title"), accessor: (record) => { // +2 for seller
        if(record["pending_new_update?"]){
          return(
            <div className="flex alignItemsCenter">
              <span className="mr10">{ record.title }</span>
              <Tooltip content="Este producto tiene un cambio pendiente de publicación.">
                <Icon source={ EditMinor } />
              </Tooltip>
            </div>
          )
        }
        else{
          return(
            <span>{ record.title }</span>
          )
        }
      }
    },

    isAdmin ? { size: 2, header: ta("product.user"), accessor: (record) =>  <span>{record.user.name} ({record.user.vendor})</span> } : null, // -2 for seller
    
    { size: 1, header: ta("general.quantity"), accessor: "total_inventory" },
    
    { size: 2, header: ta("product.status"), accessor: (record) =>
      <Badge status={ statusSlugs[record.status] }>
        { I18n.t(`enum.status.${record.status}`) }
      </Badge>
    },

    { size: 2, header: ta("product.apb"),
      accessor: (record) =>
      <Badge status={ record.apb ? 'success' : 'warning' }>
        { I18n.t(String(record.apb)) }
      </Badge>
    },
  ];

  return resizeAndFilterColumns({ columns, resize: !isAdmin, resizeAttribute: "sellerSize" })
}

export const orderSuppliesColumns = (isAdmin) => {
  const columns = [
    { size: 1, header: "#",  accessor: "display_bright_pearl_id"  },
    { size: 1, header: I18n.t("activerecord.attributes.order_supply.created_at"), accessor: "created_at_text" },
    { size: 2, header: I18n.t("activerecord.attributes.order_supply.line_items_ids"), accessor: "quantity" },
    { size: 2, header: I18n.t("activerecord.attributes.order_supply.status"),
      accessor: (record) =>
      <div>
        <Badge status={ statusSlugs[record.status] }>{ I18n.t(`enum.status.${record.status}`) }</Badge>
      </div>
    },
    // Admin ==>
    isAdmin ? { size: 2, header: I18n.t("activerecord.attributes.order_supply.vendor"), accessor: "vendor" } : null,
    isAdmin ? { size: 1, header: I18n.t("activerecord.attributes.order_supply.user_id"),
      accessor: (record) =>
      <div>
        <Badge status={ !!record.user_id ? "info" : "warning" }>
          { !!record.user_id ? I18n.t("true") : I18n.t("false") }
        </Badge>
      </div>
    } : null,
    { size: 1, header: I18n.t("activerecord.attributes.order_supply.email_sent"), accessor: (record) => 
      <div>
        <Badge status={ !!record.email_sent ? "info" : "warning" }>
          { !!record.email_sent ? I18n.t("true") : I18n.t("false") }
        </Badge>
      </div>
    },
    { size: 1, header: I18n.t("activerecord.attributes.order_supply.delivery_task_id"), accessor: (record) => 
      <div>
        <Badge status={ !!record.delivery_task_id ? "info" : "warning" }>
          { !!record.delivery_task_id ? I18n.t("true") : I18n.t("false") }
        </Badge>
      </div>
    },
    { size: 1, header: I18n.t("activerecord.attributes.order_supply.intcomex_po_id"), accessor: (record) =>
      <div>
        <Badge status={ !!record.intcomex_po_id ? "info" : "warning" }>
          { !!record.intcomex_po_id ? I18n.t("true") : I18n.t("false") }
        </Badge>
      </div>
    }
  ]
  return reject(columns, (item) => item == null);
}

export const dispatchControlColumns = [
  { size: 1, header: ta("dispatch_control.id"), accessor: "display_id" },
  { size: 3, header: I18n.t("activerecord.models.courier.one"), accessor: (record) => record.courier.name },
  { size: 3, header: ta("dispatch_control.created_at"), accessor: "created_at_text" },
]

export const courierColumns = [
  { size: 4, header: ta("courier.name"), accessor: "name" },
  { size: 4, header: ta("courier.migration_handle"), accessor: "migration_handle" },
]

export const saleReportColumns = [
  { size: 1, header: ta("general.id"), accessor: "display_id" }, // calculated on serializer
  { size: 5, header: ta("sale_report.date_range"), accessor: "date_range" }, // calculated on serializer
  { size: 4, header: ta("sale_report.total_amount"), 
    accessor: (saleReport) => {
      return saleReport.totals_money ? join(saleReport.totals_money, " / ") : "-";
    },
  }
];

export const sectionColumns = [
  { size: 4, header: ta("general.id"), accessor: "id" },
  { size: 4, header: ta("general.title"), accessor: "title" }, // calculated on serializer
  { size: 4, header: ta("section.identifier"), accessor: "identifier" }
];

export const userColumns = [
  { size: 2, header: ta('user.name'), accessor: 'name' },
  { size: 2, header: ta('user.email'), accessor: 'email' },
  { size: 1, header: ta('user.role'),
    accessor: (record) => {
      return I18n.t(`enum.role.${record.role}`)
    }
  },
  { size: 3, header: ta('seller.vendor'), accessor: 'vendor' },
  { size: 2, header: ta('seller.legal_id'),
    accessor: (record) =>{
      if(record.seller){
        return record.seller.legal_id
      }
    }
  },
  { size:2, header: "",
    accessor: (record) => {
    if(record.apu){
      return <Badge status="info">APU</Badge>
      }
    }
  },
]

export const sellerApplicationFormColumns = [
  { size: 1, header: ta("general.id"), accessor: "id" },
  { size: 2, header: ta("general.status"), accessor: (obj) => 
    <div>
      <Badge status={ statusSlugs[obj.status] }>{ I18n.t(`enum.status.${obj.status}`) }</Badge>
    </div>
  },
  { size: 2, header: ta("general.name"), accessor: (obj) => `${obj.first_name} ${obj.last_name}` }, // calculated on serializer
  { size: 2, header: ta("general.email"), accessor: "email" },
  { size: 3, header: ta("seller_application_form.company_name"), accessor: "company_name" },
  { size: 2, header: ta("general.created_at"), accessor: (obj) => I18n.l("time.formats.long", obj.created_at) },
];


export const returnIndexColumns = [
  { size: 2, header: "ID", accessor: "id" },
  // { size: 3, header: ta("general.date"), accessor: (obj) => I18n.l("time.format.long", obj.date) },// inconsistent! Sometimes load with en locale
  { size: 3, header: ta("general.date"), accessor: (obj) => obj.date_human }, 
  { size: 5, header: ta("general.amount"), accessor: "amount_money" },
];

export const resourceTableColumns = [
  { size: 2, header: ta("general.id"), accessor: "id" },
  { size: 4, header: ta("general.title"), accessor: "title" },
  { size: 2, header: ta("resource_table.identifier"), accessor: "identifier" },
  { size: 4, header: ta("resource_table.tags"), accessor: (item) => (
    <Stack>
      { item.tags.map( (tag, index) => <Tag key={index}>{ tag }</Tag> ) }
    </Stack>
  )},
];